/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import { useRef } from 'react';
import type { ReactNode } from 'react';

import { Typography, breakpoints } from '@coursera/cds-core';
import type { Theme } from '@coursera/cds-core';

import GrowthDiscoveryExperiments from 'bundles/epic/clients/GrowthDiscovery';
import useUpdateCtaPosition from 'bundles/unified-description-page-common/hooks/useUpdateCtaPosition';

type Props = {
  partnerLogo?: ReactNode;
  productTitle: string;
  description?: ReactNode;
  instructors?: ReactNode;
  primaryCta?: ReactNode;
  secondaryCta?: ReactNode;
  additionalInfo?: ReactNode;
  additionalCta?: ReactNode;
};

const styles = {
  section: (theme: Theme) => css`
    padding: ${theme.spacing(48, 0)};
    ${breakpoints.down('sm')} {
      padding: ${theme.spacing(24, 0)};
    }
  `,
  logo: (theme: Theme) => css`
    margin-top: ${theme.spacing(16)};
    margin-bottom: ${theme.spacing(16)};
    ${breakpoints.down('sm')} {
      margin-top: 0;
      margin-bottom: ${theme.spacing(12)};
    }
  `,
  instructors: (theme: Theme) => css`
    margin-top: ${theme.spacing(24)};
  `,
  description: (theme: Theme) => css`
    margin-top: ${theme.spacing(16)};
    ${breakpoints.down('sm')} {
      margin-top: ${theme.spacing(12)};
    }
  `,
  ctas: css`
    display: flex;
    align-items: baseline;
    margin-top: var(--cds-border-radius-400);

    :empty {
      display: none;
    }

    ${breakpoints.down('sm')} {
      flex-direction: column;
      align-items: normal;
      gap: var(--cds-border-radius-200);

      button,
      a,
      form {
        width: 100% !important;
        max-width: 100%;
      }
    }
  `,
  // This has the unfortunate job of adjusting styling for consumer and enterprise enroll buttons.
  // We import these buttons from other bundles so don't have direct control over styling.
  primaryCta: css`
    button,
    a,
    form {
      width: auto !important;
    }
    ${breakpoints.down('sm')} {
      button,
      a,
      form {
        width: 100% !important;
        max-width: 100%;
      }
    }
  `,
  additionalCta: css`
    margin-top: var(--cds-spacing-600);
    ${breakpoints.down('sm')} {
      margin-top: var(--cds-spacing-400);
    }
  `,
  biggerAdditionalCta: css`
    margin-top: var(--cds-spacing-100);
  `,
};

const HeroModule = ({
  productTitle,
  partnerLogo,
  description,
  instructors,
  primaryCta,
  secondaryCta,
  additionalInfo,
  additionalCta,
}: Props) => {
  const pageCtaRef = useRef<HTMLDivElement | null>(null);
  useUpdateCtaPosition(pageCtaRef);

  const shouldShowBiggerCTA = GrowthDiscoveryExperiments.get('showBiggerCTAonXDP');

  return (
    <section css={styles.section} data-e2e="hero-module">
      {partnerLogo && <div css={styles.logo}>{partnerLogo}</div>}
      <Typography data-e2e="hero-title" variant="d2semibold" component="h1">
        {productTitle}
      </Typography>
      {description && <div css={styles.description}>{description}</div>}
      {instructors && <div css={styles.instructors}>{instructors}</div>}
      <div css={styles.ctas}>
        {primaryCta && (
          <div css={styles.primaryCta} ref={pageCtaRef}>
            {primaryCta}
          </div>
        )}
        {secondaryCta}
      </div>
      {additionalInfo && <div css={styles.description}>{additionalInfo}</div>}
      {additionalCta && (
        <div css={shouldShowBiggerCTA ? styles.biggerAdditionalCta : styles.additionalCta}>{additionalCta}</div>
      )}
    </section>
  );
};

export default HeroModule;
